/**
 * One read serves every sub-tab, because they all sit behind the same date controls and share
 * the metrics row.
 *
 * Two early exits: a register that does not cover the matter, and one that closed before the
 * matter was registrable at all. Collapsing them loses a real distinction between "never" and
 * "not yet".
 */

import { Outlet, useLocation } from 'react-router';
import { useScreen } from '~/layout/RouteAnnouncer';
import { QueryError } from '~/layout/ErrorBoundary';
import { Tabs, TabPanel } from '~/ui/Tabs';
import { MetricGrid, MetricTile, SkeletonStack } from '~/ui/Surface';
import { Callout, EmptyState } from '~/ui/Feedback';
import { routes } from '~/lib/routes';
import { fmtDate, fmtPeriod, lowerLabel, plural } from '~/lib/format';
import { useMatterView } from './useMatterView';
import { KnownDatesBar } from './KnownDatesBar';
import { TemporalBanner } from './TemporalBanner';
import { QueueActionButton } from '../queue/QueueActionButton';
import { vocabularyOf, governingVersionOf } from './describe';
import styles from './Matter.module.css';

const PANEL_ID = 'matter-panel';

/**
 * Tabs that still say something when the register holds no rule text. The gap analysis is the
 * clearest case: a register with nothing recorded is exactly the one whose analysis matters, and
 * swallowing it behind the empty state tells the reader there is nothing to see.
 */
const SPEAKS_WHEN_EMPTY = new Set(['sources', 'gaps']);

export function MatterLayout() {
  const view = useMatterView();
  const { pathname } = useLocation();
  const { panel, meta, code, matter, dates, life } = view;

  useScreen(panel.data ? `${panel.data.label} — ${code}` : null);

  if (panel.isError) {
    return <QueryError error={panel.error} onRetry={() => void panel.refetch()} />;
  }
  if (panel.isPending || meta.isPending) {
    return <SkeletonStack rows={6} height={52} />;
  }

  const data = panel.data;
  const vocab = vocabularyOf(meta.data);

  if (data.summary === null) {
    return (
      <div className={styles.notCovered}>
        <p>
          This register does not register {lowerLabel(data.label)}. That is a fact about the
          register rather than a gap in the research: no rule, no deadline and no filing window
          exists for it here.
        </p>
      </div>
    );
  }

  const withheldCount = data.rules.filter(
    (rule) => governingVersionOf(rule)?.contradiction != null,
  ).length;

  const sub = pathname.split('/').at(-1) ?? 'timeline';

  const termVersion = data.rules
    .map((rule) => ({ rule, version: governingVersionOf(rule) }))
    .find((entry) => entry.rule.shape === 'term' || entry.rule.shape === 'annuity');

  const term = termVersion?.version
    ? fmtPeriod(
        termVersion.version.firstTerm ?? termVersion.version.maxTerm,
        vocab.unitLabels,
      )
    : '—';

  const tabs = [
    { key: 'timeline', label: 'Timeline', href: routes.timeline(code, matter, dates) },
    {
      key: 'rules',
      label: 'Rules',
      href: routes.rules(code, matter, dates),
      count: data.rules.length,
    },
    {
      key: 'sources',
      label: 'Official sources',
      href: routes.sources(code, matter),
      count: data.sources.length,
    },
    { key: 'gaps', label: 'Gap analysis', href: routes.gaps(code, matter) },
  ];

  // Read off the tab list rather than a second copy of its keys: a tab present in one and absent
  // from the other leaves the bar highlighting a tab the reader is not on.
  const selected = tabs.some((tab) => tab.key === sub) ? sub : 'rules';

  return (
    <>
      <TemporalBanner asAt={data.asAt} recordedAt={data.recordedAt} historical={data.historical} />

      {/* No constituting rule means no filing window, which is not the same as nothing to show:
          the rules the register does hold are still held, and hiding them behind this contradicts
          the count on the tab that got the reader here. */}
      {data.filingWindow === null ? (
        <Callout tone="warn" title={`No rule constituting a ${lowerLabel(data.label)} right`}>
          <p>
            Without one there is no window of filing dates this register can answer for, so no
            deadline on this screen is calculated against a filing. The earliest date{' '}
            {lowerLabel(data.label)} are admissible here is {fmtDate(data.floor.date)}
            {data.floor.note ? ` — ${data.floor.note}` : ''}.
          </p>
          {data.floor.citation ? <p>Authority: {data.floor.citation}</p> : null}
        </Callout>
      ) : null}

      <KnownDatesBar
        matterLabel={data.label}
        filing={data.filing}
        filingClamped={data.filingClamped}
        filingWindow={data.filingWindow}
        floor={data.floor}
        dates={dates}
        onChange={view.setDates}
        action={
          <QueueActionButton
            code={code}
            name={code}
            matters={[matter]}
            matterVocabulary={meta.data?.matters ?? []}
            openEntry={data.openQueueEntry}
            label={`Queue ${lowerLabel(data.label)} research`}
          />
        }
      />

      <MetricGrid>
        <MetricTile value={data.summary.rules} label="Rules held" />
        <MetricTile value={data.summary.versions} label="Versions on record" />
        <MetricTile value={term} label="Term for this filing" />
        <MetricTile value={data.sources.length} label="Official sources" />
      </MetricGrid>

      {withheldCount > 0 ? (
        <Callout tone="issue" title={`${plural(withheldCount, 'version')} withheld`} alert>
          The engine&rsquo;s own checking contradicts {withheldCount === 1 ? 'a version' : 'these versions'},
          so {withheldCount === 1 ? 'it is' : 'they are'} excluded from every calculation on this
          screen until someone clears {withheldCount === 1 ? 'it' : 'them'}. Each refusal is shown
          where it happens, with what the research found.
        </Callout>
      ) : null}

      {(life?.warnings.length ?? 0) > 0 ? (
        <Callout tone="warn" title="Worth checking">
          <ul>
            {(life?.warnings ?? []).map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <Tabs
        tabs={tabs}
        value={selected}
        size="sub"
        aria-label={`${data.label} at ${code}`}
        panelId={PANEL_ID}
        idPrefix="matter-subtab"
      />
      <TabPanel id={PANEL_ID} labelledBy={`matter-subtab-${selected}`}>
        {data.summary.versions === 0 && !SPEAKS_WHEN_EMPTY.has(selected) ? (
          <EmptyState>
            <p>
              No {lowerLabel(data.label)} rule text has been recorded for this register yet. Add one
              on the Rules tab, or queue a research run and review what it proposes — nothing is
              written until a human confirms it.
            </p>
          </EmptyState>
        ) : (
          <Outlet />
        )}
      </TabPanel>
    </>
  );
}
